import { ArtistImage } from "models/ArtistImage";
import { useEffect, useState } from "react";
import { ApiService } from "services/ApiService";
import ArtistImageComponent from "./ArtistImageComponent";

interface ArtistImagesProps {
    artistId: string;
    isVisitor: boolean;
    refreshKey: number;
    onClick?: (image: ArtistImage) => void;
    //onDeleteClick?: (image: ArtistImage) => void;
}

const ArtistImages: React.FC<ArtistImagesProps> = ({ artistId, isVisitor, refreshKey, onClick }) => {
    const [artistImages, setArtistImages] = useState<ArtistImage[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);

    useEffect(() => {
        const fetchImages = async () => {
            const response = await ApiService.getArtistImages(artistId);

            if (response.success && response.data) setArtistImages(response.data);
        };

        fetchImages();
    }, [artistId, refreshKey]); // Re-fetch when refreshKey changes

    const handleNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % artistImages.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prevIndex) =>
            (prevIndex - 1 + artistImages.length) % artistImages.length
        );
    };

    const handleImageClick = (image: ArtistImage) => {
        //setSelectedImage(image);
        if (onClick) onClick(image); // Call the onClick prop if provided
    };

    const handleImageUpdate = (updatedImage: ArtistImage) => {
        setArtistImages((prevImages) => 
            prevImages.map((img) => 
                img.id === updatedImage.id ? updatedImage : img
            )
        );
    };

    const handleDeleteClick = async (image: ArtistImage): Promise<void> => {
        try {
            const response = await ApiService.deleteArtistImage(artistId,image.id);

            if (response.success) {
                // Remove the deleted image from the state
                setArtistImages((prevImages) => {
                    const updatedImages = prevImages.filter((img) => img.id !== image.id);

                    // Adjust the currentIndex to ensure it points to a valid image
                    if (currentIndex >= updatedImages.length) {
                        setCurrentIndex(Math.max(0, updatedImages.length - 1)); // Move to the last valid index
                    }

                    return updatedImages;
                });
            }
        } catch (error) {
            console.error("Error deleting artist image:", error);
        }
    }


    return (
        <div className="relative w-full h-screen bg-gray-100">
            {artistImages.length > 0 && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <ArtistImageComponent
                        artistImage={artistImages[currentIndex]} // Use currentIndex to determine the current image
                        artistId={artistId}
                        isVisitor={isVisitor}
                        onClick={() => handleImageClick(artistImages[currentIndex])} // Pass click handler
                        onDeleteClick={() => handleDeleteClick(artistImages[currentIndex])} // Pass delete handler
                        onImageUpdate={handleImageUpdate} // Pass update handler
                        isEditable={!isVisitor}
                    />
                </div>
            )}

            {/* Navigation Buttons */}
            {artistImages.length > 1 && (
                <>
                    <button
                        onClick={handlePrev}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none"
                    >
                        &#8592;
                    </button>
                    <button
                        onClick={handleNext}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none"
                    >
                        &#8594;
                    </button>
                </>
            )}
        </div>
    );
};

export default ArtistImages;